import type { DestinationsContainer } from '#containers/DestinationsContainer.js';
import type { Destination } from '#models/DestinationModel.js';

import { getErrorReporter } from '#lib/error-reporting.js';
import { createState } from '#lib/signals-utils.js';

/**
 * State management for destination search and selection
 */
export function createDestinationSearchState(destinations: DestinationsContainer) {
  const { ClientDestination } = destinations;

  // Lazy query creation - create query on first access to avoid Firestore initialization issues
  let allDestinationsQuery: ReturnType<typeof ClientDestination.list> | undefined;

  const state = createState({
    /** Search input value */
    searchQuery: '',
    /** Currently selected destination IDs */
    selectedDestinationIds: [] as string[],
    /** Initial selected IDs (for comparison to detect changes) */
    initialSelectedIds: [] as string[],

    /** Get the destinations query (lazy initialization) */
    get destinationsQuery() {
      if (!allDestinationsQuery) {
        allDestinationsQuery = ClientDestination.list({});
      }
      return allDestinationsQuery;
    },

    /** Get all destinations from the query, filtering out any that fail validation */
    get allDestinations(): Destination[] {
      const query = this.destinationsQuery;
      if (!query.isReady) return [];
      
      // Filter out invalid destinations, catching any validation errors
      const validDestinations: Destination[] = [];
      for (const dest of query.items) {
        // Skip if destination is null/undefined
        if (!dest) continue;
        
        try {
          // Access properties to trigger validation if it hasn't happened yet
          // If the destination is invalid, accessing properties might throw
          const id = dest.id;
          const name = dest.name;
          
          // Only include destinations with valid id and name
          if (id !== undefined && name) {
            validDestinations.push(dest);
          }
        } catch (error) {
          // Log validation errors but continue processing other destinations
          const destinationId = (dest as { id?: string })?.id || 'unknown';
          console.warn(`[DestinationSearchState] Skipping invalid destination (${destinationId}):`, error);
          getErrorReporter().captureException(error);
        }
      }
      return validDestinations;
    },

    /** Whether destinations are still loading */
    get isLoading(): boolean {
      return !this.destinationsQuery.isReady;
    },

    /** Filtered destinations matching search query, excluding already selected */
    get filteredDestinations(): Destination[] {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return [];

      const all = this.allDestinations.filter((dest): dest is Destination => dest !== undefined);
      const selectedSet = new Set(this.selectedDestinationIds);

      // Filter destinations that match the search query
      const matches = all.filter((dest) => {
        // Skip if already selected
        if (dest.id && selectedSet.has(dest.id)) return false;

        // Match against name
        if (dest.name?.toLowerCase().includes(query)) return true;

        // Match against alternate names
        // if (dest.alternateNames?.some((name) => name.toLowerCase().includes(query))) return true;

        // Match against country name
        if (dest.location?.countryName?.toLowerCase().includes(query)) return true;

        // Match against continent name
        if (dest.location?.continent?.toLowerCase().includes(query)) return true;

        // Match against state/province name
        if (dest.location?.stateProvince?.toLowerCase().includes(query)) return true;

        // Match against travel region name
        if (dest.location?.travelRegion?.toLowerCase().includes(query)) return true;

        return false;
      });

      // Limit results for performance
      return matches.slice(0, 15);
    },

    /** Full destination objects for selected IDs */
    get selectedDestinations(): Destination[] {
      const selectedSet = new Set(this.selectedDestinationIds);
      return this.allDestinations.filter((dest): dest is Destination => dest !== undefined && dest.id !== undefined && selectedSet.has(dest.id));
    },

    /** Update search query */
    setSearchQuery(query: string) {
      this.searchQuery = query;
    },

    /** Add destination to selection (prevents duplicates) */
    addDestination(destinationId: string) {
      if (!this.selectedDestinationIds.includes(destinationId)) {
        this.selectedDestinationIds = [...this.selectedDestinationIds, destinationId];
      }
    },

    /** Remove destination from selection */
    removeDestination(destinationId: string) {
      this.selectedDestinationIds = this.selectedDestinationIds.filter((id) => id !== destinationId);
    },

    /** Initialize with existing selection */
    setSelectedIds(ids: string[]) {
      this.selectedDestinationIds = [...ids];
      this.initialSelectedIds = [...ids];
    },

    /** Check if there are unsaved changes */
    get hasChanges(): boolean {
      if (this.selectedDestinationIds.length !== this.initialSelectedIds.length) return true;
      const currentSet = new Set(this.selectedDestinationIds);
      const initialSet = new Set(this.initialSelectedIds);
      return !this.selectedDestinationIds.every((id) => initialSet.has(id)) || 
             !this.initialSelectedIds.every((id) => currentSet.has(id));
    },

    /** Reset state to initial values */
    reset() {
      this.searchQuery = '';
      this.selectedDestinationIds = [];
      this.initialSelectedIds = [];
    },
  });

  return state;
}

export type DestinationSearchState = ReturnType<typeof createDestinationSearchState>;
